import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowDown, ArrowUp, Plus, Trash2, Save } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Aurora } from "@/components/motion/Aurora";

export default function ReturnChecklist() {
  const [items, setItems] = useState([]);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/return-checklist");
      setItems(data.items || []);
      setEmailEnabled(Boolean(data.email_enabled));
    } catch (err) {
      toast.error(formatError(err));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (i, k, v) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const move = (i, dir) =>
    setItems((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const next = [...arr];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { items: items.map((it, i) => ({ ...it, sort_order: (i + 1) * 10 })) };
      const { data } = await api.put("/admin/return-checklist", payload);
      setItems(data.items || []);
      toast.success("Checklist guardada");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aurora-page relative isolate min-h-screen bg-ivory">
      <Aurora variant="subtle" fixed />
      <header className="bg-white border-b border-sand">
        <div className="container-x py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-warmgray hover:text-charcoal" data-testid="checklist-back"><ArrowLeft className="h-5 w-5" /></Link>
            <div>
              <h1 className="font-display text-xl font-bold text-charcoal">Checklist de devolución</h1>
              <p className="text-sm text-warmgray">Lo que el cliente debe revisar antes de devolver la furgoneta</p>
            </div>
          </div>
          <Button onClick={save} disabled={saving} className="bg-forest-deep hover:bg-forest text-white" data-testid="checklist-save">
            <Save className="h-4 w-4 mr-2" />{saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </header>

      <main className="container-x py-6 max-w-4xl space-y-4">
        <div className={`rounded-xl border px-4 py-3 text-sm ${emailEnabled ? "bg-ok/10 border-ok/30 text-forest" : "bg-warn/10 border-warn/30 text-warn-ink"}`} data-testid="checklist-email-status">
          {emailEnabled
            ? "Email de preparación ACTIVO: 24 h antes de cada devolución el cliente recibe el enlace a su checklist."
            : "Email de preparación DESACTIVADO: los clientes siguen recibiendo el recordatorio clásico. Se activa desde la configuración del servidor (RETURN_PREP_EMAIL_ENABLED) cuando lo apruebes."}
        </div>

        <div className="bg-white border border-sand rounded-xl p-5">
          <div className="hidden sm:grid grid-cols-[28px_1fr_1fr_60px_84px] gap-3 text-xs uppercase tracking-wide text-warmgray-light px-1 mb-2">
            <span></span><span>Texto (español)</span><span>Text (català)</span><span>Activa</span><span></span>
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={it.id || i} className="grid sm:grid-cols-[28px_1fr_1fr_60px_84px] gap-3 items-center bg-ivory border border-sand rounded-lg p-2" data-testid={`checklist-row-${i}`}>
                <span className="text-xs text-warmgray-light text-center">{i + 1}</span>
                <Input value={it.label_es} onChange={(e) => set(i, "label_es", e.target.value)} placeholder="Ej.: Vaciar las aguas grises" />
                <Input value={it.label_ca} onChange={(e) => set(i, "label_ca", e.target.value)} placeholder="Ex.: Buidar les aigües grises" />
                <div className="flex justify-center"><Checkbox checked={it.active !== false} onCheckedChange={(v) => set(i, "active", Boolean(v))} /></div>
                <div className="flex gap-1 justify-end">
                  <Button variant="ghost" size="icon" onClick={() => move(i, -1)} aria-label="Subir"><ArrowUp className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => move(i, 1)} aria-label="Bajar"><ArrowDown className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="text-terracotta" onClick={() => setItems((arr) => arr.filter((_, j) => j !== i))} aria-label="Eliminar"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
          <Button variant="outline" className="mt-4" onClick={() => setItems((arr) => [...arr, { id: "", label_es: "", label_ca: "", active: true }])} data-testid="checklist-add">
            <Plus className="h-4 w-4 mr-2" />Añadir tarea
          </Button>
          <p className="text-xs text-warmgray-light mt-3">Si dejas el catalán vacío se usará el texto en español. Las tareas desactivadas no se muestran al cliente.</p>
        </div>
      </main>
    </div>
  );
}
